import { useEffect, useState, memo, useCallback } from "react";
import axios from "axios";
import { getToken } from "../../utils/auth";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useTranslation } from "react-i18next";
import StorageMonitor from "./components/StorageMonitor";
import ConfigPanel from "./components/ConfigPanel";
import CONFIG from "../../config";

const Stat = memo(({ title, value }) => (
    <div className="border rounded p-4 text-center">
        <div className="text-gray-500 text-sm">{title}</div>
        <div className="text-2xl font-bold">{value}</div>
    </div>
));

const AdminAnalytics = memo(() => {
    const [stats, setStats] = useState(null);
    const [mode, setMode] = useState("stats");

    const token = getToken();
    const { t, i18n } = useTranslation();



    const fetchStats = useCallback(() => {
        axios.get(`${CONFIG.API_URL}/api/admin/analytics`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
        }).then(res => setStats(res.data)).catch(err => {
            console.error(err);
        });
    }, [token]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    if (!stats) return <div className="p-6">{t('admin.common.loading')}</div>;

    return (
        <div className="max-w-7xl mx-auto my-12 p-8 bg-white rounded-xl shadow-lg border border-gray-100">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
                <h2 className="text-3xl font-bold text-gray-800">{t('admin.analytics_page.title')}</h2>
                <div className="flex items-center gap-4">

                    <button
                        onClick={() => window.history.back()}
                        className="flex items-center gap-2 text-gray-500 hover:text-brand-primary transition-all text-sm font-medium group"
                    >
                        <span className={`transition-transform group-hover:-translate-x-1 ${i18n.language?.startsWith('ar') ? "rotate-180 group-hover:translate-x-1" : ""}`}>←</span>
                        {t('admin.common.back', 'Back')}
                    </button>
                </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <Stat title={t('admin.analytics_page.total_reviews')} value={stats.total_reviews || 0} />
                <Stat title={t('admin.analytics_page.avg_rating')} value={Number(stats.avg_rating || 0).toFixed(2)} />
                <Stat title={t('admin.analytics_page.pending_reviews')} value={stats.pending_reviews || 0} />
                <Stat title={t('admin.analytics_page.feedback_messages')} value={stats.feedback_count || 0} />
                <Stat title={t('admin.analytics_page.helpful_votes')} value={stats.helpful_votes || 0} />
                <Stat title={t('admin.analytics_page.total_views')} value={stats.total_views || 0} />
            </div>

            {/* Monitors */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <StorageMonitor />
                <ConfigPanel />
            </div>

            <div className="space-y-2">
                <button
                    onClick={() => setMode(m => m === "stats" ? "charts" : "stats")}
                    className="mb-6 bg-gray-200 px-4 py-2 rounded mt-6 block"
                >
                    {mode === "stats" ? t('admin.analytics_page.switch_charts') : t('admin.analytics_page.switch_stats')}
                </button>
                {mode === "stats" ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <Stat title={t('admin.analytics_page.total_reviews')} value={stats.total_reviews} />
                        <Stat title={t('admin.analytics_page.avg_rating')} value={stats.avg_rating} />
                        <Stat title={t('admin.analytics_page.pending_reviews')} value={stats.pending_reviews} />
                        <Stat title={t('admin.analytics_page.helpful_votes')} value={stats.helpful_votes} />
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={stats.top_products}>
                            <XAxis dataKey="name" />
                            <Tooltip />
                            <Bar dataKey="avg_rating" fill="#facc15" />
                        </BarChart>
                    </ResponsiveContainer>
                )}
                <h3 className="text-lg font-semibold mb-3 mt-8">{t('admin.analytics_page.top_rated')}</h3>
                {(stats.top_products || []).map(p => (
                    <div key={p.id} className="flex justify-between border p-3 rounded">
                        <span>{p.name}</span>
                        <span className="text-yellow-500">
                            ★ {Number(p.avg_rating || 0).toFixed(1)} ({p.reviews_count || 0})
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
});

export default AdminAnalytics;
